
import { store } from '../store/store';
import { Page } from '../store/supaPagesSlice';
import { createSafeExternalUrl } from './urlUtils';

/**
 * Get the pricing page URL for a given slug from the Redux store
 * @param slug The company or page slug to look up
 * @returns The pricing page URL, or null if not found
 */
export const getCompanyPricingPageUrl = (slug: string): string | null => {
  const state = store.getState();
  const pages = state.supaPages.pages;
  
  // First try to find an exact match for the slug (page-level slug)
  const exactPage = pages.find(page => page.slug === slug);
  if (exactPage?.pricingPageUrl) {
    return createSafeExternalUrl(exactPage.pricingPageUrl);
  }
  
  // Check for snake_case property
  if (exactPage?.pricing_page_url) {
    return createSafeExternalUrl(exactPage.pricing_page_url);
  }
  
  // If no exact match, check if the slug is a company slug (prefix of any page slug)
  const companyPages = pages.filter(page => page.slug.startsWith(slug));
  if (companyPages.length > 0) {
    // Sort by slug length to prioritize the main page (usually shortest slug)
    const sortedPages = companyPages.sort((a, b) => a.slug.length - b.slug.length);
    
    // Return the first page with a pricing URL
    const pageWithUrl = sortedPages.find(page => page.pricingPageUrl || page.pricing_page_url);
    if (pageWithUrl?.pricingPageUrl) {
      return createSafeExternalUrl(pageWithUrl.pricingPageUrl);
    }
    if (pageWithUrl?.pricing_page_url) {
      return createSafeExternalUrl(pageWithUrl.pricing_page_url);
    }
  }
  
  console.log(`🔍 [getCompanyPricingPageUrl] No pricing page URL found for slug: ${slug}`);
  return null;
};

/**
 * Get the pricing page URL for multiple slugs
 * @param slugs Array of slugs to look up
 * @returns Object mapping slugs to their pricing page URLs (null if not found)
 */
export const getCompanyPricingPageUrls = (slugs: string[]): Record<string, string | null> => {
  const result: Record<string, string | null> = {};
  
  for (const slug of slugs) {
    result[slug] = getCompanyPricingPageUrl(slug);
  }
  
  return result;
};
